"use client";
import { HeartIcon, SearchIcon } from "@/assets/icons";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useShoppingCart } from "use-shopping-cart";
import GlobalSearchModal from "../Common/GlobalSearch";
import CustomSelect from "./CustomSelect";
import MobileDropdown from "./MobileDropdown";
import { menuData } from "./menuData";
import { useAppSelector } from "@/redux/store";
import { UserIcon } from "../MyAccount/icons";
import { useTranslations } from "next-intl";
import { useDispatch } from "react-redux";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import { initializeAuth } from "@/redux/features/auth-slice";
import { useStoreStatus } from "../StoreStatusProvider/StoreStatusProvider";
import StoreClosedBanner from "../StoreClosed/StoreClosedBanner";
import { isUnoptimizable } from "@/utils/image";

const Header = (props: { host: string | null }) => {
  const { host } = props;
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [stickyMenu, setStickyMenu] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const ecommerce = useEcommerce();
  const { isOpen, isPaused } = useStoreStatus();

  const t = useTranslations();
  const pathname = usePathname();

  const { handleCartClick, cartCount, totalPrice } = useShoppingCart();

  const dispatch = useDispatch();

  const clientProfile = useAppSelector(
    (state) => state.authReducer.clientProfile
  );
  const isAuthenticated = useAppSelector(
    (state) => state.authReducer.isAuthenticated
  );

  const avatarSrc = clientProfile?.avatarBase64 ?? "/images/avatar.jpeg";
  const logoSrc = ecommerce.logo || "/images/logo/logo.svg";

  const isLoggedIn = !!isAuthenticated;
  const wasLoggedIn = useRef(false);
  useEffect(() => {
    const loggingOut =
      !isLoggedIn && wasLoggedIn.current && pathname?.startsWith("/my-account");
    const loggingIn =
      isLoggedIn && !wasLoggedIn.current && pathname === "/signin";
    setIsAccountLoading(loggingOut || loggingIn);
    wasLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, pathname]);

  const getDisplayName = () => {
    if (!isLoggedIn) return t("Account.subtitle");

    const fullName = [clientProfile?.firstName, clientProfile?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    if (fullName) return fullName;
    if (clientProfile?.email) return clientProfile.email;

    return t("Account.title");
  };

  const displayName = getDisplayName();

  const handleOpenCartModal = () => {
    handleCartClick();
  };

  const handleStickyMenu = () => {
    if (window.scrollY >= 80) {
      setStickyMenu(true);
    } else {
      setStickyMenu(false);
    }
  };

  useEffect(() => {
    setIsMounted(true);
    dispatch(initializeAuth());
    window.addEventListener("scroll", handleStickyMenu);

    return () => {
      window.removeEventListener("scroll", handleStickyMenu);
    };
  }, [dispatch]);

  return (
    <>
      <header
        className={`sticky left-0 top-0 w-full z-999 bg-white transition-all ease-in-out duration-300 ${
          stickyMenu && "shadow-sm"
        }`}
      >
        {!isOpen && <StoreClosedBanner paused={isPaused} />}
        <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 xl:px-0">
          {/* <!-- header top start --> */}
          <div className="flex flex-col gap-2 ease-out duration-200 py-4">
            {/* Logo - mobile only */}
            <div className="flex items-center justify-center w-full lg:hidden">
              <Link className="shrink-0 flex gap-3 items-center" href="/">
                <Image
                  src={logoSrc}
                  alt="Logo"
                  width={100}
                  height={100}
                  priority
                  unoptimized={isUnoptimizable(logoSrc)}
                  className="w-20 h-20 object-contain"
                />
                <div className="text-lg font-semibold">{ecommerce.name}</div>
              </Link>
            </div>

            {/* Search bar with cart - mobile */}
            <div className="w-full lg:hidden">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="flex gap-5 items-center">
                  <div className="relative w-full">
                    <input
                      onClick={() => {
                        setSearchModalOpen(true);
                      }}
                      defaultValue={""}
                      type="search"
                      name="search"
                      id="search"
                      placeholder={t("SearchNav.title")}
                      autoComplete="off"
                      className="custom-search w-full rounded-full bg-gray-1 border border-gray-3 h-[42px] py-2.5 pl-5 pr-10 outline-hidden ease-in duration-200 text-sm placeholder:text-xs sm:text-base sm:placeholder:text-sm"
                    />
                    <button
                      type="submit"
                      id="search-btn"
                      aria-label="Search"
                      className="absolute flex items-center h-[42px] justify-center duration-200 ease-in -translate-y-1/2 right-5 top-1/2 hover:text-blue"
                    >
                      <SearchIcon className="w-5 h-5 text-gray-6" />
                    </button>
                  </div>
                  <Link
                    href={isLoggedIn ? "/my-account" : "/signin"}
                    className="flex items-center justify-center w-10 h-10 shrink-0"
                    aria-label="Account"
                  >
                    <div className="flex items-center justify-center w-9 h-9 border border-gray-3 rounded-full overflow-hidden">
                      {isAccountLoading ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-3 border-t-blue" />
                      ) : (
                        <Image
                          src={avatarSrc}
                          alt="avatar"
                          width={36}
                          height={36}
                          className="object-cover w-full h-full"
                          unoptimized={isUnoptimizable(avatarSrc)}
                        />
                      )}
                    </div>
                  </Link>

                  {/* Cart button - mobile inline with search */}
                  <button
                    onClick={handleOpenCartModal}
                    className="flex items-center gap-2.5 w-10 h-10 justify-center shrink-0"
                  >
                    <span className="relative inline-block">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M12 4V5C12 6.65685 10.6569 8 9 8C7.34315 8 6 6.65685 6 5V4M2.5 17H15.5C16.3284 17 17 16.3284 17 15.5V2.5C17 1.67157 16.3284 1 15.5 1H2.5C1.67157 1 1 1.67157 1 2.5V15.5C1 16.3284 1.67157 17 2.5 17Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="flex items-center justify-center font-medium text-2xs absolute -right-2 -top-2.5 bg-red w-4.5 h-4.5 rounded-full text-white">
                        {cartCount}
                      </span>
                    </span>
                  </button>
                </div>
              </form>
            </div>

            {/* Desktop layout */}
            <div className="hidden lg:flex lg:flex-row gap-5 items-center xl:justify-between">
              {/* <!-- header top left --> */}
              <div className="flex flex-col w-full gap-5 xl:w-auto sm:flex-row xl:justify-between sm:items-center sm:gap-10">
                <Link className="shrink-0 flex items-center gap-5" href="/">
                  <Image
                    src={logoSrc}
                    alt="Logo"
                    width={100}
                    height={100}
                    priority
                    unoptimized={isUnoptimizable(logoSrc)}
                  />
                  <div className="font-semibold text-xl">{ecommerce.name}</div>
                </Link>

                <div className="max-w-[475px] w-full">
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="flex gap-2 items-center">
                      <CustomSelect />

                      <div className="relative lg:min-w-[370px] w-full">
                        <input
                          onClick={() => {
                            setSearchModalOpen(true);
                          }}
                          defaultValue={""}
                          type="search"
                          name="search"
                          id="search"
                          placeholder={t("SearchNav.title")}
                          autoComplete="off"
                          className="custom-search  w-full rounded-full bg-gray-1  border border-gray-3 h-[42px] py-2.5 pl-5 pr-10 outline-hidden ease-in duration-200"
                        />
                        <button
                          type="submit"
                          id="search-btn"
                          aria-label="Search"
                          className="absolute flex items-center h-[42px] justify-center duration-200 ease-in  -translate-y-1/2 right-5 top-1/2 hover:text-blue"
                        >
                          <SearchIcon className="w-5 h-5 text-gray-6" />
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="flex w-full lg:w-auto items-center gap-7.5">
                <div className="flex items-center justify-between w-full gap-8 lg:w-auto">
                  <div className="items-center gap-5 flex">
                    <Link
                      href={isLoggedIn ? "/my-account" : "/signin"}
                      className="hidden lg:flex items-center gap-2.5 shrink-0 text-left"
                    >
                      {isAccountLoading ? (
                        <div className="flex items-center gap-2.5">
                          <div className="flex items-center justify-center w-9 h-9 border border-gray-3 rounded-full">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-3 border-t-blue" />
                          </div>
                          <div className="hidden xl:block">
                            <div className="h-2.5 w-16 bg-gray-3 rounded animate-pulse mb-1.5" />
                            <div className="h-3 w-24 bg-gray-3 rounded animate-pulse" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-center w-9 h-9 border border-gray-3 rounded-full overflow-hidden">
                            <Image
                              src={avatarSrc}
                              alt="avatar"
                              width={36}
                              height={36}
                              className="object-cover w-full h-full rounded-full"
                              unoptimized={isUnoptimizable(avatarSrc)}
                            />
                          </div>
                          <div className="group hidden xl:block">
                            <span className="block uppercase font-medium text-2xs text-dark-4">
                              {isLoggedIn
                                ? t("Account.title")
                                : t("Account.login")}
                            </span>
                            <p className="font-medium text-custom-sm text-dark hover:text-blue">
                              {displayName}
                            </p>
                          </div>
                        </>
                      )}
                    </Link>

                    <div className="flex items-center gap-2.5">
                      {/* <LanguageSwitcher /> */}

                      {/* Cart */}
                      <button
                        onClick={handleOpenCartModal}
                        className="flex items-center gap-2.5 w-8 h-8 justify-center"
                      >
                        <span className="relative inline-block">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M12 4V5C12 6.65685 10.6569 8 9 8C7.34315 8 6 6.65685 6 5V4M2.5 17H15.5C16.3284 17 17 16.3284 17 15.5V2.5C17 1.67157 16.3284 1 15.5 1H2.5C1.67157 1 1 1.67157 1 2.5V15.5C1 16.3284 1.67157 17 2.5 17Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="flex items-center justify-center font-medium text-2xs absolute -right-2 -top-2.5 bg-red w-4.5 h-4.5 rounded-full text-white">
                            {cartCount}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!-- header top end --> */}
        </div>

        {/* Offcanvas Mobile Menu (Mobile Only) */}
        <div
          className={`fixed inset-0 z-[998] xl:hidden transition-all duration-300 ${navigationOpen ? "visible opacity-100" : "invisible opacity-0"}`}
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-dark/70 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setNavigationOpen(false)}
          ></div>
          {/* Sidebar */}
          <aside
            className={`fixed top-0 right-0 z-[999] h-full w-80 max-w-full bg-white shadow-2xl flex flex-col transition-all duration-300 ease-out transform ${
              navigationOpen ? "translate-x-0" : "translate-x-full"
            }`}
            style={{
              transitionDelay: navigationOpen ? "0ms" : "50ms",
            }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-3">
              <Link href="/">
                <Image
                  src="/images/logo/logo.svg"
                  alt="Logo"
                  width={130}
                  height={28}
                  unoptimized
                />
              </Link>
              <button
                aria-label="Close menu"
                className="w-10 h-10 bg-transparent text-dark-2 rounded-lg inline-flex items-center cursor-pointer justify-center hover:bg-gray-2"
                onClick={() => setNavigationOpen(false)}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  transform="rotate(0 0 0)"
                >
                  <path
                    d="M6.21967 7.28033C5.92678 6.98744 5.92678 6.51256 6.21967 6.21967C6.51256 5.92678 6.98744 5.92678 7.28033 6.21967L11.999 10.9384L16.7176 6.2198C17.0105 5.92691 17.4854 5.92691 17.7782 6.2198C18.0711 6.51269 18.0711 6.98757 17.7782 7.28046L13.0597 11.999L17.7782 16.7176C18.0711 17.0105 18.0711 17.4854 17.7782 17.7782C17.4854 18.0711 17.0105 18.0711 16.7176 17.7782L11.999 13.0597L7.28033 17.7784C6.98744 18.0713 6.51256 18.0713 6.21967 17.7784C5.92678 17.4855 5.92678 17.0106 6.21967 16.7177L10.9384 11.999L6.21967 7.28033Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              <ul className="flex flex-col gap-1">
                {menuData.map((menuItem, i) =>
                  menuItem.submenu ? (
                    <MobileDropdown
                      key={i}
                      menuItem={menuItem}
                      onClose={() => setNavigationOpen(false)}
                    />
                  ) : (
                    <li
                      key={i}
                      className={`transform transition-all duration-300 ease-out ${
                        navigationOpen
                          ? "translate-x-0 opacity-100"
                          : "translate-x-4 opacity-0"
                      }`}
                      style={{
                        transitionDelay: navigationOpen ? `${i * 50}ms` : "0ms",
                      }}
                    >
                      <Link
                        href={menuItem.path!}
                        className="flex items-center gap-2 text-sm font-medium text-dark py-2 px-3 rounded-md hover:bg-blue/10 hover:text-blue transition-colors"
                        onClick={() => setNavigationOpen(false)}
                      >
                        {menuItem.title}
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </nav>{" "}
            <div className="px-3 py-4 border-t space-y-1 border-gray-3 flex flex-col gap-2">
              <Link
                href={isLoggedIn ? "/my-account" : "/signin"}
                className={`flex items-center gap-2 text-dark px-2 py-1 hover:bg-blue/10 rounded-md hover:text-blue font-medium text-sm transition-all duration-300 ease-out transform ${
                  navigationOpen
                    ? "translate-x-0 opacity-100"
                    : "translate-x-4 opacity-0"
                }`}
                style={{
                  transitionDelay: navigationOpen
                    ? `${menuData.length * 50}ms`
                    : "0ms",
                }}
                onClick={() => setNavigationOpen(false)}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full">
                  {isAccountLoading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-3 border-t-blue" />
                  ) : (
                    <UserIcon className="w-5 h-5" />
                  )}
                </span>
                {displayName}
              </Link>
              <Link
                href="/wishlist"
                className={`flex items-center gap-2 text-dark hover:text-blue px-2 py-1 hover:bg-blue/10 rounded-md font-medium text-sm transition-all duration-300 ease-out transform ${
                  navigationOpen
                    ? "translate-x-0 opacity-100"
                    : "translate-x-4 opacity-0"
                }`}
                style={{
                  transitionDelay: navigationOpen
                    ? `${(menuData.length + 1) * 50}ms`
                    : "0ms",
                }}
                onClick={() => setNavigationOpen(false)}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full relative">
                  <HeartIcon className="w-5 h-5" />
                </span>
                Wishlist
              </Link>
            </div>
          </aside>
        </div>
      </header>

      {searchModalOpen && (
        <GlobalSearchModal
          host={host}
          searchModalOpen={searchModalOpen}
          setSearchModalOpen={setSearchModalOpen}
        />
      )}
    </>
  );
};

export default Header;
